import { DeleteOutlined } from '@ant-design/icons';
import { Button, Flex, Form, Input, Space, Tooltip } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Shipment } from '@/model/Logistic/Shipment/Shipment';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

interface ModifyDeliveryNumbersModalProps {
  record?: Shipment;
  refetch?: () => void;
  closeModal: () => void;
}

/**
 * Componente extraído de ShipmentShowLogistic.tsx. Se extrae para mejorar la mantenibilidad
 */
const ModifyDeliveryNumbersModal = (props: ModifyDeliveryNumbersModalProps) => {
  const { t } = useTranslation();
  const { record, closeModal, refetch } = props;
  const { shipmentRepository } = useRepositoriesContext();
  const [form] = Form.useForm();
  const [newDeliveryNumber, setNewDeliveryNumber] = useState(
    record?.delivery_note?.split(',') || [],
  );
  const onConfirmChange = useCallback(() => {
    if (record?.id) {
      if (newDeliveryNumber.join(',') === record?.delivery_note) {
        return;
      } else if (newDeliveryNumber !== undefined) {
        void shipmentRepository
          .updateShipment(record?.id, { delivery_note: newDeliveryNumber.join(',') })
          .then(() => {
            closeModal();
            refetch?.();
          });
      }
    }
  }, [record, newDeliveryNumber, shipmentRepository, closeModal, refetch]);
  const deliveryInputValue = Form.useWatch('deliveryNumber', form);

  return (
    <div className="ModifyDeliveryNumberModal">
      <Form
        form={form}
        onFinish={values => {
          setNewDeliveryNumber(prevValue =>
            values.deliveryNumber === '' || values.deliveryNumber === undefined
              ? prevValue
              : [...prevValue, values.deliveryNumber],
          );
          form.resetFields();
        }}
        layout="vertical">
        <Form.Item
          name="deliveryNumber"
          rules={[
            {
              validateTrigger: 'onBlur',
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }

                if (newDeliveryNumber.includes(value)) {
                  return Promise.reject(new Error(t('El número de remito ya existe en la lista')));
                }

                return Promise.resolve();
              },
            },
          ]}>
          <Space.Compact style={{ width: '100%' }}>
            <Input />
            <Button htmlType="submit"> + </Button>
          </Space.Compact>
        </Form.Item>
        <ul>
          {newDeliveryNumber.map(number => (
            <li key={number}>
              <div className="DeliveryNumberItem">
                {number}{' '}
                <Button
                  onClick={() =>
                    setNewDeliveryNumber(prevValue => prevValue.filter(n => n !== number))
                  }
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                />
              </div>
            </li>
          ))}
        </ul>
        <Flex justify="end" gap={8}>
          <Button onClick={closeModal}>{t('Cancelar')}</Button>
          <Tooltip
            title={
              deliveryInputValue !== undefined && deliveryInputValue !== ''
                ? t('Finalice la edición del numero de remito para guardar')
                : ''
            }>
            <Button
              onClick={onConfirmChange}
              type="primary"
              disabled={deliveryInputValue !== undefined && deliveryInputValue !== ''}>
              {t('Guardar')}
            </Button>
          </Tooltip>
        </Flex>
      </Form>
    </div>
  );
};

export default ModifyDeliveryNumbersModal;
